// Catalog.ID Client — Hybrid KEM (X25519 + ML-KEM-1024)
// ============================================================================
// Two-layer key encapsulation: classical X25519 ECDH + post-quantum ML-KEM-1024.
// Combined via HKDF-SHA-512 so an attacker must break both layers.
// See: whitepapers/Hybrid Cryptography Briefing.md §2.2
//
// Caller-supplied HKDF `info` and AEAD `aad`:
//   - `info` is a required parameter — no module-level default. Every caller
//     names its protocol family explicitly so a future protocol can't be silently
//     accepted by a handler scoped to a different protocol. The Catalog protocol
//     family values are exported as `HKDF_INFO_CID_KEYBAG`, `HKDF_INFO_EFS_WRAP`,
//     etc. — extend as new protocols land.
//   - `aad` is a required parameter; pass an empty Uint8Array for "no AAD".
//     The AEAD authenticates `(combined_secret, nonce, plaintext, aad)`; the
//     decapsulator must supply byte-identical `aad` to recover the plaintext.

import { ml_kem1024 } from '@noble/post-quantum/ml-kem.js';
import { x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha512 } from '@noble/hashes/sha2.js';

export const HYBRID_KEM_VERSION = 0x01;

/** Length of an X25519 public key — the classical half of a hybrid public key. */
export const X25519_PK_LEN = 32;
const X25519_SK_LEN = 32;
const MLKEM_PK_LEN = 1568;
const MLKEM_SK_LEN = 3168;
const MLKEM_CT_LEN = 1568;

export const HYBRID_PUBLIC_KEY_LEN = X25519_PK_LEN + MLKEM_PK_LEN;   // 1600
export const HYBRID_SECRET_KEY_LEN = X25519_SK_LEN + MLKEM_SK_LEN;   // 3200

// version(1) + epk(32) + ctPq(1568) + salt(32) + nonce(12) = 1645
export const HYBRID_KEM_HEADER_BYTES = 1 + X25519_PK_LEN + MLKEM_CT_LEN + 32 + 12;

/** HKDF `info` for cid-internal keybag / claim-key wraps. */
export const HKDF_INFO_CID_KEYBAG = new TextEncoder().encode('catalog-hybrid-kem-v1');

/** HKDF `info` for EFS package-key / KEK wraps (EFS WP §12.3). */
export const HKDF_INFO_EFS_WRAP = new TextEncoder().encode('catalog-efs-wrap-v1');

/**
 * HKDF `info` for chat epoch wraps — conversation epoch keys (contact-flow.md
 * §5) AND read-state sync keys (message-wire.md §6, formally an epoch key of
 * the self sync stream). One label covers both because both are epoch keys of
 * some stream; the two AAD FAMILIES tell them apart:
 *   - `utf8("<operatorId>/<channelId>/<epoch>")` — a conversation epoch,
 *   - `utf8("<cid>/chat-sync/<keyId>")` — the self read-state sync stream.
 * Deliberately distinct from `catalog-efs-wrap-v1` and
 * `catalog-hybrid-kem-v1`: chat has its own keys for compromise scoping, and
 * sharing a KDF label would give some of that back.
 */
export const HKDF_INFO_CHAT_EPOCH_WRAP =
    new TextEncoder().encode('catalog-chat-epoch-wrap-v1');
/**
 * HKDF `info` for the `export-mail-encrypt` ceremony: the account's
 * `mail_encrypt` private key(s), wrapped to the requesting mail module's
 * EPHEMERAL wrap key (mail migration §5.1, operational machine custody). AAD
 * is `utf8("<cid>/mail-encrypt-export/<fingerprint>")`. Its own label, like
 * every family here: a blob minted for this export must not open under a
 * decapsulator built for anything else, and vice versa.
 */
export const HKDF_INFO_MAIL_ENCRYPT_EXPORT =
    new TextEncoder().encode('catalog-mail-encrypt-export-v1');

/**
 * HKDF `info` for `bitcash-wallets-release`: the member's whole wallet document
 * (`cid/bitcash-wallets`, cid-encrypted-storage §7.2/§7.3), wrapped to the
 * bitcash module's EPHEMERAL wrap key so that machine can spend from the
 * account's wallets. AAD is `utf8("<cid>/bitcash-wallets-release/<generation>")`
 * — the GENERATION, not a fingerprint, because what is released is one version
 * of a mutable document and the manager must be able to tell a replayed older
 * release from the current one.
 */
export const HKDF_INFO_BITCASH_WALLETS_RELEASE =
    new TextEncoder().encode('catalog-bitcash-wallets-release-v1');

/**
 * HKDF `info` for `bitcash-wallets-save`: ONE wallet travelling the other way —
 * the module hands the vault a wallet it minted or imported, wrapped to a
 * CID-side ephemeral key obtained from `bitcash-wallets-save-prepare`. AAD is
 * `utf8("<cid>/bitcash-wallets-save/<prepareRequestId>")`, which binds the blob
 * to the one prepare leg that minted the key it opens under.
 *
 * Its OWN label, distinct from the release's: a blob minted for one direction
 * must never open under the decapsulator built for the other, and the two
 * directions carry different things (a whole document vs one wallet).
 */
export const HKDF_INFO_BITCASH_WALLETS_SAVE =
    new TextEncoder().encode('catalog-bitcash-wallets-save-v1');

export interface KemKeyPair {
    publicKey: Uint8Array;   // 1600 bytes: x25519_pk(32) || mlkem_pk(1568)
    secretKey: Uint8Array;   // 3200 bytes: x25519_sk(32) || mlkem_sk(3168)
}

/** Generate a hybrid X25519 + ML-KEM-1024 key pair. */
export function kemKeygen(): KemKeyPair {
    const classical = x25519.utils.randomSecretKey();
    const classicalPub = x25519.getPublicKey(classical);
    const pq = ml_kem1024.keygen();

    const publicKey = new Uint8Array(HYBRID_PUBLIC_KEY_LEN);
    publicKey.set(classicalPub, 0);
    publicKey.set(pq.publicKey, X25519_PK_LEN);

    const secretKey = new Uint8Array(HYBRID_SECRET_KEY_LEN);
    secretKey.set(classical, 0);
    secretKey.set(pq.secretKey, X25519_SK_LEN);

    return { publicKey, secretKey };
}

/**
 * Hybrid encapsulate: produce a combined shared secret encrypted to a hybrid public key.
 * Returns an AES-256-GCM encrypted payload as:
 *   version(1) || epk(32) || ctPq(1568) || salt(32) || nonce(12) || aesCt(payload + 16 tag)
 *
 * @param publicKey  1600-byte combined hybrid pubkey
 * @param payload    plaintext bytes to wrap
 * @param info       HKDF `info` domain separator (e.g. `HKDF_INFO_CID_KEYBAG`)
 * @param aad        AES-GCM additional authenticated data (empty `Uint8Array` for none)
 */
export async function kemEncapsulate(
    publicKey: Uint8Array,
    payload: Uint8Array,
    info: Uint8Array,
    aad: Uint8Array,
): Promise<Uint8Array> {
    if (publicKey.length !== HYBRID_PUBLIC_KEY_LEN)
        throw new Error(`Hybrid KEM: expected ${HYBRID_PUBLIC_KEY_LEN}-byte public key, got ${publicKey.length}`);

    const pkClassical = publicKey.slice(0, X25519_PK_LEN);
    const pkPq = publicKey.slice(X25519_PK_LEN);

    // Classical ECDH
    const ephemeralSk = x25519.utils.randomSecretKey();
    const epk = x25519.getPublicKey(ephemeralSk);
    const sClassical = x25519.getSharedSecret(ephemeralSk, pkClassical);
    ephemeralSk.fill(0);

    // Post-quantum KEM
    const { cipherText: ctPq, sharedSecret: sPq } = ml_kem1024.encapsulate(pkPq);

    // Combine via HKDF-SHA-512
    const ikm = new Uint8Array(64);
    ikm.set(sClassical, 0);
    ikm.set(sPq, 32);
    const salt = crypto.getRandomValues(new Uint8Array(32));
    const combinedKey = hkdf(sha512, ikm, salt, info, 32);

    // AES-256-GCM encrypt payload. Casts to BufferSource silence TS strictness:
    // @noble/hashes returns Uint8Array<ArrayBufferLike>, but WebCrypto demands
    // Uint8Array<ArrayBuffer> (excluding SharedArrayBuffer). Runtime is identical.
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const cryptoKey = await crypto.subtle.importKey('raw', combinedKey as BufferSource, { name: 'AES-GCM' }, false, ['encrypt']);
    const aesCt = new Uint8Array(await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: nonce, additionalData: aad as BufferSource },
        cryptoKey,
        payload as BufferSource,
    ));

    // Assemble: version || epk || ctPq || salt || nonce || aesCt
    const result = new Uint8Array(HYBRID_KEM_HEADER_BYTES + aesCt.length);
    let offset = 0;
    result[offset++] = HYBRID_KEM_VERSION;
    result.set(epk, offset); offset += X25519_PK_LEN;
    result.set(ctPq, offset); offset += MLKEM_CT_LEN;
    result.set(salt, offset); offset += 32;
    result.set(nonce, offset); offset += 12;
    result.set(aesCt, offset);

    return result;
}

/**
 * Hybrid decapsulate: recover payload from a hybrid KEM ciphertext.
 *
 * @param secretKey   3200-byte combined hybrid secret
 * @param ciphertext  wire blob produced by `kemEncapsulate`
 * @param info        HKDF `info` (must match encapsulation byte-for-byte)
 * @param aad         AES-GCM AAD (must match encapsulation byte-for-byte)
 */
export async function kemDecapsulate(
    secretKey: Uint8Array,
    ciphertext: Uint8Array,
    info: Uint8Array,
    aad: Uint8Array,
): Promise<Uint8Array> {
    if (secretKey.length !== HYBRID_SECRET_KEY_LEN)
        throw new Error(`Hybrid KEM: expected ${HYBRID_SECRET_KEY_LEN}-byte secret key, got ${secretKey.length}`);
    if (ciphertext.length < HYBRID_KEM_HEADER_BYTES + 16) // at least header + GCM tag
        throw new Error(`Hybrid KEM: ciphertext too short (${ciphertext.length} bytes)`);
    if (ciphertext[0] !== HYBRID_KEM_VERSION)
        throw new Error(`Hybrid KEM: unsupported version byte 0x${ciphertext[0].toString(16).padStart(2, '0')}`);

    const skClassical = secretKey.slice(0, X25519_SK_LEN);
    const skPq = secretKey.slice(X25519_SK_LEN);

    // Parse ciphertext
    let offset = 1;
    const epk = ciphertext.slice(offset, offset + X25519_PK_LEN); offset += X25519_PK_LEN;
    const ctPq = ciphertext.slice(offset, offset + MLKEM_CT_LEN); offset += MLKEM_CT_LEN;
    const salt = ciphertext.slice(offset, offset + 32); offset += 32;
    const nonce = ciphertext.slice(offset, offset + 12); offset += 12;
    const aesCt = ciphertext.slice(offset);

    // Classical ECDH
    const sClassical = x25519.getSharedSecret(skClassical, epk);

    // Post-quantum KEM
    const sPq = ml_kem1024.decapsulate(ctPq, skPq);

    // Combine via HKDF-SHA-512
    const ikm = new Uint8Array(64);
    ikm.set(sClassical, 0);
    ikm.set(sPq, 32);
    const combinedKey = hkdf(sha512, ikm, salt, info, 32);

    // AES-256-GCM decrypt
    const cryptoKey = await crypto.subtle.importKey('raw', combinedKey as BufferSource, { name: 'AES-GCM' }, false, ['decrypt']);
    const plaintext = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: nonce, additionalData: aad as BufferSource },
        cryptoKey,
        aesCt,
    );

    return new Uint8Array(plaintext);
}
